import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/system";

// Custom Widgets & Hooks
import BackBtn from "../../widgets/BackBtn";
import CustomCircularP from "../../widgets/CustomCircularP";
import SearchBar from "../../widgets/searchBar";
import ExportVisitorsButton from "./ExportVisitorsButton";
import useApiEp from "../../hooks/useApiEp";

// Utilities
import { extractDate, extractTime, getErrorResponse } from "../../utils/util";

export const ScrollTableContainer = styled(TableContainer)({
  overflowX: "auto",
});

const Visitors = ({ type }) => {
  const [reload, setReload] = useState(false);
  const [page, setPage] = useState(1);
  const [eventFilter, setEventFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [sq, setSq] = useState("");

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // API query includes type, page, sq, eventFilter, sourceFilter
  const { data, isLoading, isError, error } = useApiEp(
    `/admin/get-visitors?type=${type}&page=${page}&sq=${sq}&r=${reload}&eventCategory=${eventFilter}&sourceType=${sourceFilter}`
  );

  const { data: sourceList } = useApiEp(`/event/get-visitor-sources`);

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func.apply(null, args);
      }, delay);
    };
  };

  const handleSearch = (e) => {
    setSq(e.target.value);
    setPage(1); // <--- Reset to page 1 whenever the search query changes
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleSearch = useCallback(debounce(handleSearch, 500), []);

  const pageChange = (e, page) => {
    setPage(page);
  };

  const handleEventChange = (e) => {
    setEventFilter(e.target.value);
    setPage(1);
  };

  const handleSourceChange = (e) => {
    setSourceFilter(e.target.value);
    setPage(1);
  };

  if (isError) {
    return (
      <Box mt={2}>
        <Alert severity="error">{getErrorResponse(error)}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box mb={2}>
        <BackBtn />
      </Box>

      <CustomCircularP show={isLoading} />

      {/* Header, Filters, and Export Actions */}
      <Grid container spacing={2} mb={3} alignItems="center">
        {/* Title Section */}
        <Grid item xs={12} md={3}>
          <Stack direction="row" gap={2} alignItems="baseline">
            <Typography variant="h5" fontWeight="bold">
              {type.charAt(0).toUpperCase() + type.slice(1)}s
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Count: {data?.totalItems || 0}
            </Typography>
          </Stack>
        </Grid>

        {/* Filters Section */}
        <Grid item xs={12} sm={8} md={6}>
          <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
            <FormControl
              size="small"
              sx={{ minWidth: { xs: "100%", sm: "120px" } }}
            >
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceFilter}
                label="Source"
                onChange={handleSourceChange}
              >
                <MenuItem value="">
                  <em>All Sources</em>
                </MenuItem>
                {Array.isArray(sourceList)
                  ? sourceList.map((source, index) => (
                      <MenuItem key={index} value={source}>
                        {source}
                      </MenuItem>
                    ))
                  : sourceList?.data?.map((source, index) => (
                      <MenuItem key={index} value={source}>
                        {source}
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>

            <Box flex={1}>
              <SearchBar
                placeholder="Search by name or email..."
                size="small"
                onChange={debouncedHandleSearch}
                fullWidth
              />
            </Box>
          </Stack>
        </Grid>

        {/* Export Section */}
        <Grid item xs={12} sm={4} md={3}>
          <Stack
            direction="row"
            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
          >
            <ExportVisitorsButton filter={type} sourceFilter={sourceFilter} />
          </Stack>
        </Grid>
      </Grid>

      {/* Data Table */}
      <Box>
        <ScrollTableContainer
          component={Paper}
          elevation={2}
          sx={{ borderRadius: 2 }}
        >
          <Table stickyHeader aria-label="visitors table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: "grey.50", fontWeight: "bold" }}>
                  Photo
                </TableCell>
                <TableCell sx={{ bgcolor: "grey.50", fontWeight: "bold" }}>
                  Name
                </TableCell>
                <TableCell sx={{ bgcolor: "grey.50", fontWeight: "bold" }}>
                  Email
                </TableCell>
                <TableCell sx={{ bgcolor: "grey.50", fontWeight: "bold" }}>
                  Phone
                </TableCell>
                <TableCell sx={{ bgcolor: "grey.50", fontWeight: "bold" }}>
                  Country
                </TableCell>
                <TableCell sx={{ bgcolor: "grey.50", fontWeight: "bold" }}>
                  State
                </TableCell>
                <TableCell sx={{ bgcolor: "grey.50", fontWeight: "bold" }}>
                  City
                </TableCell>
                <TableCell sx={{ bgcolor: "grey.50", fontWeight: "bold" }}>
                  Joined
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!data?.data || data?.data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No visitors found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data?.data?.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Avatar src={user.profile_image} alt={user.firstName}>
                        {user.firstName ? user.firstName[0].toUpperCase() : "U"}
                      </Avatar>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Link
                        to={`/visitor-view/${user._id}`}
                        style={{
                          textDecoration: "none",
                          color: theme.palette.primary.main,
                          fontWeight: 500,
                        }}
                      >
                        {user.title} {user.firstName} {user.lastName}
                      </Link>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {user.mobileNo}
                    </TableCell>
                    <TableCell>{user.country?.name || "-"}</TableCell>
                    <TableCell>{user.state?.name || "-"}</TableCell>
                    <TableCell>{user.city?.name || "-"}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {extractDate(user.createdOn)} <br />
                      <Typography variant="caption" color="text.secondary">
                        {extractTime(user.createdOn)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollTableContainer>
      </Box>

      {/* Pagination */}
      {data && data.totalItems > 0 && (
        <Box display="flex" justifyContent="center" mt={4} mb={2}>
          <Pagination
            page={page}
            onChange={pageChange}
            count={data.totalPages || 1} // <--- Improved: Using totalPages from your backend API
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
};

export default Visitors;
